'use client';

/**
 * Employee Portal Layout — themed via /v1/portal-settings/public.
 *
 * workspaceId resolution strategy (chosen: query-param + sessionStorage
 * fallback + authenticated recipient fallback):
 *
 *   1. First visit — link lands employee on `/portal/login?ws=<workspaceId>`.
 *      Layout reads the `ws` query param and persists it to
 *      `sessionStorage['hf-portal:ws-id']` so subsequent navigations keep
 *      the theme.
 *   2. Return visits — layout reads from `sessionStorage`.
 *   3. Authenticated recipient — if the portal JWT was already issued,
 *      `portalAuthApi.getRecipient().workspaceId` is the source of truth
 *      and is copied back into sessionStorage.
 *   4. No workspaceId known — layout renders default HF theme (portal is
 *      never fully blocked by theming failure).
 *
 * This file intentionally does NOT render <html>/<body> — the root
 * app/layout.tsx owns those and already wires QueryProvider, Toaster,
 * theme, etc. Nested layouts in Next.js App Router must delegate to the
 * root shell; otherwise hydration breaks + providers are duplicated.
 */

import { Suspense, useEffect, useMemo, useState } from 'react';
import { useSearchParams, usePathname } from 'next/navigation';
import {
  usePublicPortalSettings,
  usePublicPortalThemes,
} from '@/components/portal/hooks/use-portal-settings';
import { portalAuthApi } from '@/lib/api/portal-auth';
import type { PortalTheme } from '@/lib/types/portal-settings';
import { CelebrationProvider } from './_components/celebrations/celebration-provider';

const WS_STORAGE_KEY = 'hf-portal:ws-id';

// Canonical BE URL — single env var across the codebase. Drop the
// non-canonical `NEXT_PUBLIC_BE_ORIGIN` alias (never set in any .env).
const beOrigin =
  process.env.NEXT_PUBLIC_API_URL ?? 'http://localhost:4001';

// Mirrors the canonical Light theme palette (`modern-teal` seed @
// src/database/seed/portal-global/data/portal-themes.json) so a portal
// rendered without a resolved `workspaceId` (anonymous visit to
// /portal/login without `?ws=`, or a workspace whose settings 404)
// falls back to pure B&W instead of the legacy teal. Fixed 2026-05-14
// — the previous teal default (#065D66 / #0891B2) was leftover from
// the pre-d4105263 multi-theme era ("reduce to Light + Dark only,
// pure B&W (ticket #190)").
const DEFAULT_THEME = {
  primaryColor: '#000000',
  secondaryColor: '#525252',
  backgroundColor: '#ffffff',
  textColor: '#0a0a0a',
  headerColor: '#ffffff',
};

function useResolvedWorkspaceId(): string {
  const searchParams = useSearchParams();
  const [workspaceId, setWorkspaceId] = useState<string>('');

  useEffect(() => {
    if (typeof window === 'undefined') return;
    // 1. Query-param wins — overwrites storage so admins can re-theme by link.
    const fromQuery = searchParams?.get('ws') ?? '';
    if (fromQuery) {
      window.sessionStorage.setItem(WS_STORAGE_KEY, fromQuery);
      setWorkspaceId(fromQuery);
      return;
    }
    // 2. Session storage — set by previous visit.
    const fromStorage = window.sessionStorage.getItem(WS_STORAGE_KEY);
    if (fromStorage) {
      setWorkspaceId(fromStorage);
      return;
    }
    // 3. Authenticated recipient — copy into storage for next nav.
    const recipient = portalAuthApi.getRecipient();
    if (recipient?.workspaceId) {
      window.sessionStorage.setItem(WS_STORAGE_KEY, recipient.workspaceId);
      setWorkspaceId(recipient.workspaceId);
    }
  }, [searchParams]);

  return workspaceId;
}

function findTheme(
  themes: PortalTheme[] | undefined,
  themeId: string | null,
): PortalTheme | null {
  if (!themes || !themeId) return null;
  return themes.find((t) => t.id === themeId) ?? null;
}

export default function PortalLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  // useSearchParams triggers SSG bailout without a Suspense boundary.
  return (
    <Suspense fallback={<PortalLayoutFallback>{children}</PortalLayoutFallback>}>
      <PortalLayoutInner>{children}</PortalLayoutInner>
    </Suspense>
  );
}

function PortalLayoutFallback({
  children,
}: {
  children: React.ReactNode;
}) {
  return (
    <div
      className="portal-shell min-h-screen"
      style={
        {
          '--portal-primary': DEFAULT_THEME.primaryColor,
          '--portal-secondary': DEFAULT_THEME.secondaryColor,
          '--portal-bg': DEFAULT_THEME.backgroundColor,
          '--portal-text': DEFAULT_THEME.textColor,
          '--portal-header': DEFAULT_THEME.headerColor,
          backgroundColor: DEFAULT_THEME.backgroundColor,
          color: DEFAULT_THEME.textColor,
        } as React.CSSProperties
      }
    >
      <main className="container max-w-5xl py-8">{children}</main>
    </div>
  );
}

function PortalLayoutInner({
  children,
}: {
  children: React.ReactNode;
}) {
  const workspaceId = useResolvedWorkspaceId();
  const pathname = usePathname();
  // The dashboard route renders <PortalPreview> which owns its own header
  // + paints full-bleed. Suppress the layout chrome so we don't double up.
  // Subroutes (/portal/news, /portal/badges, etc.) keep the layout header
  // because they use the constrained <main> shell.
  const isDashboard = pathname === '/portal';
  // CelebrationProvider expects an employeeId for per-recipient dedup.
  // Recipient may not be hydrated on first render (e.g. login page) —
  // fall back to an empty string so the provider tree renders; it simply
  // won't fire events for anonymous sessions.
  const [recipientId, setRecipientId] = useState<string>('');
  useEffect(() => {
    const r = portalAuthApi.getRecipient();
    if (r?.id) setRecipientId(r.id);
  }, []);
  const {
    data: resolved,
    isLoading: settingsLoading,
    isError: settingsError,
  } = usePublicPortalSettings(workspaceId);

  // Themes fetched client-side (anon-safe variant) and joined with
  // settings.selectedTheme. Backend /v1/portal-themes/public resolves
  // the workspace's companyId server-side and returns global + company +
  // workspace themes without requiring a JWT.
  const { data: themes } = usePublicPortalThemes(workspaceId ?? '');

  const settings = resolved?.settings;
  const theme = useMemo(
    () => findTheme(themes, settings?.selectedTheme ?? null),
    [themes, settings?.selectedTheme],
  );

  const primary = theme?.primaryColor ?? DEFAULT_THEME.primaryColor;
  const secondary = theme?.secondaryColor ?? DEFAULT_THEME.secondaryColor;
  const background = theme?.backgroundColor ?? DEFAULT_THEME.backgroundColor;
  const text = theme?.textColor ?? DEFAULT_THEME.textColor;
  const header = theme?.headerColor ?? DEFAULT_THEME.headerColor;

  const heroHeading = settings?.heroHeading ?? 'Security Portal';
  const useSameLogo = settings?.useSameLogoForBoth ?? true;
  const logo = useSameLogo
    ? settings?.portalLogoLight ?? null
    : // `light` is the conservative default — actual dark-mode swap belongs in a
      // future enhancement once the portal adopts next-themes in the (portal)
      // subtree. Keeping both images resolvable downstream.
      settings?.portalLogoLight ?? null;

  const isInitialLoading = !!workspaceId && settingsLoading;

  return (
    <>
      {/* Capture SDK hooks — previously in <head>; moved here because nested
          layouts can't render <head>. Next.js hoists <meta> tags inside body
          to the head at render time. */}
      <meta
        name="hfv3-capture-context"
        content='{"module":"LMS","targetId":"","surface":"portal-dashboard"}'
      />
      <meta name="hfv3-capture-api-base" content={beOrigin} />
      {/* TODO(capture): re-enable fingerprint.js once the BE adds
          Cross-Origin-Resource-Policy: cross-origin on /v1/capture/fingerprint.js.
          The portal does not ship a capture flow today — removing to eliminate
          the NotSameOrigin CORS error. */}

      <div
        // The admin app's global CSS pins `html, body { overflow: hidden;
        // height: 100vh }` (so the admin shell sidebar + chrome stay in
        // a fixed viewport). Inheriting that on the live portal blocks
        // scrolling for end-users — ticket #218. Following the same
        // workaround as `app/(portal-preview)/layout.tsx`: pin h-screen
        // + opt-in to vertical scroll via overflow-y-auto so the portal
        // gets its own scrollable region inside the admin overflow lock.
        // Fixed 2026-05-14.
        className="portal-shell h-screen w-screen overflow-y-auto overflow-x-hidden"
        style={
          {
            '--portal-primary': primary,
            '--portal-secondary': secondary,
            '--portal-bg': background,
            '--portal-text': text,
            '--portal-header': header,
            backgroundColor: background,
            color: text,
          } as React.CSSProperties
        }
      >
        {!isDashboard && (
          <header
            className="border-b px-6 py-3 flex items-center justify-between"
            // text color must follow theme.textColor — hardcoded `#ffffff`
            // worked when headerColor was always dark (legacy teal default),
            // but Light theme now uses headerColor=#ffffff which made the
            // brand + nav text white-on-white invisible. Fixed 2026-05-14.
            style={{ backgroundColor: header, color: text }}
          >
            <div className="flex items-center gap-3">
              {logo ? (
                // Using a plain <img> so the public endpoint can ship any
                // http(s) URL without pre-configuring next/image domains.
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={logo}
                  alt={heroHeading}
                  className="h-8 w-auto max-w-[160px] object-contain"
                />
              ) : (
                <span className="font-semibold text-lg">HumanFirewall</span>
              )}
              <span className="text-sm opacity-90">{heroHeading}</span>
            </div>
            {/* Nav only renders once the recipient is authenticated. Before
                login (e.g. on /portal/login itself), the nav links would
                404 anyway since their routes require a portal JWT.
                Fixed 2026-05-14 per product clarification. */}
            {recipientId ? (
              <nav className="flex items-center gap-4 text-sm">
                <a href="/portal/training" className="hover:underline opacity-90">
                  My Training
                </a>
                <a
                  href="/portal/certificates"
                  className="hover:underline opacity-90"
                >
                  Certificates
                </a>
              </nav>
            ) : null}
          </header>
        )}

        <main
          className={
            isDashboard
              ? 'w-full' /* PortalPreview paints full-bleed */
              : 'container mx-auto max-w-5xl py-8'
          }
        >
          {isInitialLoading ? (
            <div
              className="flex items-center justify-center py-24"
              role="status"
              aria-live="polite"
            >
              <div
                className="h-10 w-10 animate-spin rounded-full border-4 border-current border-t-transparent opacity-60"
                style={{ color: primary }}
              />
              <span className="sr-only">Loading portal…</span>
            </div>
          ) : (
            // Render children even when settingsError is true — theming
            // should NEVER block the portal. We fall through to DEFAULT_THEME
            // values already resolved above.
            <CelebrationProvider employeeId={recipientId}>
              {settingsError ? (
                <div className="sr-only" role="status">
                  Portal theme unavailable — using defaults.
                </div>
              ) : null}
              {children}
            </CelebrationProvider>
          )}
        </main>
      </div>
    </>
  );
}
